import React, { useState, useEffect, useContext, useCallback, useMemo, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  NavLink,
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Authenticator, ThemeProvider, useAuthenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import styled, { createGlobalStyle } from "styled-components";
import { updateUserAttributes, fetchUserAttributes, getCurrentUser, confirmSignUp } from "aws-amplify/auth";
import { signOut } from 'aws-amplify/auth';
import awsconfig from "./aws-exports";

// Import pages e recursos. HomePage fica de import direto (e a primeira
// tela que a maioria das pessoas ve, sem flash de loading); o resto vira
// import preguicoso (React.lazy) so pra reduzir o bundle principal - hoje
// tudo ia junto no primeiro carregamento do site inteiro.
import HomePage from "./pages/HomePage";
import keynsethLogo from "./images/logo_qa.png";
import ScrollToTop from './pages/ScrollToTop';
import { getLocalTestPlan } from "./lib/localPlan";
import { isPaidPlanId, normalizePlanId } from "./data/products";
import { apiRequest } from "./lib/api";
import { LogOut as LogOutIcon, Menu as MenuIcon, X as CloseIcon, ChevronDown as ChevronDownIcon, Sun as SunIcon, Moon as MoonIcon } from "react-feather";
import BackButton from "./components/BackButton";
import InlineAlert from "./components/InlineAlert";
import { ToastProvider } from "./components/Toast";
import { ConfirmProvider } from "./components/ConfirmDialog";
import Seo from "./components/Seo";
import { SEO_META, isNoindexPath, normalizePathname } from "./lib/seoMeta";
import { trackEvent } from "./lib/analytics";

const Ebooks = lazy(() => import("./pages/Ebooks"));
const EbookReaderPage = lazy(() => import("./pages/Ebooks").then((m) => ({ default: m.EbookReaderPage })));
const Profile = lazy(() => import("./pages/Profile"));
const WhatsAppGroup = lazy(() => import("./pages/WhatsAppGroup"));
const RoadmapPage = lazy(() => import("./pages/mapa-de-carreira-qa"));
const BenefitsPage = lazy(() => import("./pages/BenefitsPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ChecklistPage = lazy(() => import("./pages/Checklist"));
const WorkspaceQA = lazy(() => import("./pages/WorkspaceQA"));
const PlannerQA = lazy(() => import("./pages/PlannerQA"));
const TemplateBug = lazy(() => import("./pages/TemplateBug"));
const ConfirmacaoPage = lazy(() => import('./pages/ConfirmacaoPage'));
const AgendamentoPage = lazy(() => import('./pages/AgendamentoPage'));
const AgendamentoConfirmadoPage = lazy(() => import('./pages/AgendamentoConfirmadoPage'));
const Assinaturas = lazy(() => import("./pages/Assinaturas"));
const ProductsHub = lazy(() => import("./pages/PaidProducts").then((m) => ({ default: m.ProductsHub })));
const PaidProductPage = lazy(() => import("./pages/PaidProducts").then((m) => ({ default: m.PaidProductPage })));
const EbookStorePage = lazy(() => import("./pages/EbookStorePage"));
const MentoriaPage = lazy(() => import("./pages/MentoriaPage"));
const CurriculoPage = lazy(() => import("./pages/CurriculoPage"));
const ArticlesPage = lazy(() => import("./pages/ArticlesPage").then((m) => ({ default: m.ArticlesPage })));
const ArticleDetailPage = lazy(() => import("./pages/ArticlesPage").then((m) => ({ default: m.ArticleDetailPage })));
const ArticleAdminPage = lazy(() => import("./pages/ArticlesPage").then((m) => ({ default: m.ArticleAdminPage })));

// Footer imports
const PoliticaPrivacidade = lazy(() => import("./pages/PoliticaPrivacidade"));
const TermosDeUso = lazy(() => import("./pages/TermosDeUso"));
const Contato = lazy(() => import("./pages/Contato"));
const FAQ = lazy(() => import("./pages/FAQ"));

// Fallback exibido enquanto o chunk da rota (lazy) esta sendo baixado.
// Minimalista de proposito - some rapido na maioria das navegacoes.
const RouteFallback = styled.div`
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #93a0be;
  font-family: "Inter", sans-serif;
  font-size: 0.9rem;
`;

const configuredSocialProviders = Array.isArray(awsconfig.aws_cognito_social_providers)
  ? awsconfig.aws_cognito_social_providers.map((provider) => String(provider).toUpperCase())
  : [];

const hasCognitoHostedUiConfig = Boolean(
  awsconfig.oauth?.domain &&
  awsconfig.oauth?.redirectSignIn &&
  awsconfig.oauth?.redirectSignOut
);

const authSocialProviders =
  hasCognitoHostedUiConfig && configuredSocialProviders.includes("GOOGLE")
    ? ["google"]
    : [];

const THEME_STORAGE_KEY = "qa_site_theme";
const ThemeModeContext = React.createContext({
  themeMode: "dark",
  toggleTheme: () => {},
});

const useThemeMode = () => useContext(ThemeModeContext);

// Custom theme for Authenticator
const customTheme = {
  name: "qa-iniciante-auth-theme",
  tokens: {
    colors: {
      brand: {
        primary: {
          10: "#e0f2fe",
          20: "#bae6fd",
          40: "#22b8d1",
          60: "#22B8D1",
          80: "#0B7F93",
          90: "#0B7F93",
          100: "#0A1424",
        },
      },
      font: {
        primary: "#17253D",
        secondary: "#57648A",
        interactive: "#0f6c9f",
        interactiveHover: "#0B7F93",
        interactiveActive: "#0A1424",
      },
    },
    components: {
      button: {
        primary: {
          backgroundColor: { value: "#22B8D1" },
          color: { value: "#ffffff" },
          borderRadius: { value: "12px" },
          _hover: {
            backgroundColor: { value: "#0B7F93" },
          },
          _focus: {
            backgroundColor: { value: "#0B7F93" },
          },
          _active: {
            backgroundColor: { value: "#0B7F93" },
          },
          _disabled: {
            backgroundColor: { value: "#d1d5db" },
            color: { value: "#9ca3af" },
          },
        },
      },
      fieldcontrol: {
        borderRadius: { value: "12px" },
        borderColor: { value: "#c7d2e8" },
        color: { value: "#17253D" },
        paddingInlineStart: { value: "16px" },
        paddingInlineEnd: { value: "16px" },
        _focus: {
          borderColor: { value: "#22B8D1" },
          boxShadow: { value: "0 0 0 4px rgba(34, 184, 209, 0.16)" },
        },
      },
      authenticator: {
        router: {
          borderWidth: { value: "0" },
          borderRadius: { value: "18px" },
          boxShadow: { value: "0 28px 90px rgba(10, 17, 30, 0.42)" },
        },
      },
    },
  },
};

// Global styles
const GlobalStyle = createGlobalStyle`
  :root {
    --app-bg: ${({ themeMode }) => (themeMode === "light" ? "#f5f7fb" : "#0B1424")};
    --app-text: ${({ themeMode }) => (themeMode === "light" ? "#17253D" : "#dbe6f5")};
    --surface-primary: ${({ themeMode }) => (themeMode === "light" ? "#ffffff" : "rgba(31, 48, 80, 0.96)")};
    --surface-secondary: ${({ themeMode }) => (themeMode === "light" ? "#f4f7fc" : "rgba(31, 48, 80, 0.9)")};
    --surface-muted: ${({ themeMode }) => (themeMode === "light" ? "#f0f9ff" : "rgba(31, 48, 80, 0.9)")};
    --border-color: ${({ themeMode }) => (themeMode === "light" ? "#dff6fa" : "rgba(148, 163, 184, 0.18)")};
    --text-primary: ${({ themeMode }) => (themeMode === "light" ? "#17253D" : "#f4f7fc")};
    --text-secondary: ${({ themeMode }) => (themeMode === "light" ? "#57648A" : "#c7d2e8")};
    --text-muted: ${({ themeMode }) => (themeMode === "light" ? "#7482a0" : "#93a0be")};
    --brand-strong: #22B8D1;
    --brand-soft: ${({ themeMode }) => (themeMode === "light" ? "rgba(34, 184, 209, 0.12)" : "rgba(34, 184, 209, 0.14)")};
    --nav-shadow: ${({ themeMode }) => (themeMode === "light" ? "0 5px 20px rgba(34, 184, 209, 0.08)" : "0 12px 36px rgba(10, 17, 30, 0.44)")};
  }

  body {
    margin: 0;
    font-family: "Inter", sans-serif;
    background: var(--app-bg);
    color: var(--app-text);
    transition: background 0.25s ease, color 0.25s ease;
    /* O drawer mobile fica fora da tela em translateX(100%) quando fechado.
       Sem isso ele gera scroll horizontal no celular (a pagina "balanca" pro
       lado e revela uma faixa vazia). */
    overflow-x: hidden;
  }

  [data-amplify-authenticator] {
    min-height: calc(100vh - 86px);
    background:
      radial-gradient(circle at top left, rgba(34, 184, 209, 0.20), transparent 30%),
      radial-gradient(circle at bottom right, rgba(108, 143, 176, 0.14), transparent 28%),
      linear-gradient(135deg, rgba(31, 48, 80, 0.86), rgba(10, 17, 30, 0.98)),
      #0B1424;
    padding: 54px 20px;
    position: relative;
    overflow: hidden;
  }

  [data-amplify-authenticator]::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(148, 163, 184, 0.05) 1px, transparent 1px),
      linear-gradient(90deg, rgba(148, 163, 184, 0.05) 1px, transparent 1px);
    background-size: 44px 44px;
    mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.75), transparent 78%);
    pointer-events: none;
  }

  [data-amplify-container] {
    background: #ffffff !important;
    border: 1px solid rgba(231, 237, 247, 0.96) !important;
    border-radius: 18px !important;
    box-shadow: 0 28px 90px rgba(10, 17, 30, 0.42) !important;
    overflow: hidden !important;
    width: min(100%, 500px) !important;
    position: relative;
    z-index: 1;
  }

  [data-amplify-router] {
    border: 0 !important;
    border-radius: 18px !important;
    overflow: hidden !important;
  }

  [data-amplify-form] {
    padding: 24px 28px 26px !important;
    gap: 14px !important;
  }

  .amplify-tabs {
    border-bottom: 1px solid #dbe6f5 !important;
    background: #f4f7fc !important;
  }

  .amplify-label,
  .amplify-tabs__item {
    color: #334155 !important;
    font-weight: 800 !important;
  }

  .amplify-tabs__item--active {
    color: #0B7F93 !important;
    border-color: #22B8D1 !important;
    background: #ffffff !important;
  }

  .amplify-input {
    background: #f4f7fc !important;
    border-color: #c7d2e8 !important;
    color: #17253D !important;
    border-radius: 12px !important;
    min-height: 48px !important;
    font-size: 0.95rem !important;
  }

  .amplify-field__description,
  .amplify-text,
  .amplify-heading {
    color: #57648A !important;
  }

  .amplify-input:focus {
    border-color: #22B8D1 !important;
    box-shadow: 0 0 0 3px rgba(34, 184, 209, 0.16) !important;
  }

  .amplify-button--primary {
    background: #22B8D1 !important;
    color: #ffffff !important;
    border-radius: 12px !important;
    font-weight: 900 !important;
    min-height: 48px !important;
    box-shadow: 0 12px 24px rgba(34, 184, 209, 0.22) !important;
  }

  .amplify-button--primary:hover {
    background: #0B7F93 !important;
  }

  .amplify-button--link {
    color: #0B7F93 !important;
    font-weight: 800 !important;
  }

  .amplify-button[data-variation="link"] {
    border-radius: 10px !important;
  }

  .amplify-button[data-variation="default"] {
    border-color: #c7d2e8 !important;
    border-radius: 12px !important;
    color: #17253D !important;
    font-weight: 850 !important;
    min-height: 46px !important;
  }

  .amplify-divider {
    color: #7482a0 !important;
  }

  .amplify-field__show-password {
    color: #0B7F93 !important;
  }

  .amplify-alert {
    border-radius: 8px !important;
  }
`;

// Styled components
const Logo = styled.img`
  width: 78px;
  height: auto;
  margin-bottom: 14px;
`;

const Title = styled.h2`
  color: #17253D;
  font-weight: 900;
  font-size: clamp(1.45rem, 4vw, 1.85rem);
  margin: 0 0 10px;
  text-align: center;
  letter-spacing: 0;
`;

const Subtitle = styled.p`
  color: #57648A;
  font-size: 1rem;
  line-height: 1.55;
  margin: 0;
  text-align: center;
`;

const Navbar = styled.nav`
  padding: 15px 30px;
  background-color: var(--surface-primary);
  box-shadow: var(--nav-shadow);
  position: sticky;
  top: 0;
  z-index: 1000;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--border-color);
  gap: 20px;

  @media (max-width: 768px) {
    padding: 12px 20px;
  }
`;

const NavHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  min-width: 0;

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

// O titulo da secao atual so existe no mobile (no desktop quem orienta e o
// item ativo do menu). Ele divide a barra com a logo e o botao do menu, entao
// precisa poder encolher e cortar com reticencias em vez de empurrar o resto
// da barra pra fora da tela - era isso que espremia os nomes no celular.
const CurrentPageTitle = styled.span`
  font-size: 1.8rem;
  color: var(--text-primary);
  margin: 0;
  font-weight: 700;
  display: none;

  @media (max-width: 768px) {
    font-size: 1.05rem;
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const MenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  color: var(--text-primary);
  z-index: 1001;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: var(--brand-soft);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(34, 184, 209, 0.3);
  }

  svg {
    width: 30px;
    height: 30px;
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

// Menu mobile (drawer). Antes ele era `height: 100vh` + `justify-content:
// center` + `gap: 25px` e SEM rolagem: com os 13 itens que o menu logado tem
// hoje, a lista media mais que a altura da tela e o excesso ficava
// literalmente cortado em cima e embaixo, sem jeito de alcancar. Agora o
// drawer rola, alinha do topo e usa 100dvh (a barra de endereco do navegador
// mobile muda a altura da viewport, e 100vh nao acompanha).
const drawerListStyles = `
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  background: linear-gradient(180deg, var(--surface-secondary) 0%, var(--surface-primary) 100%);
  box-shadow: var(--nav-shadow);
  flex-direction: column;
  gap: 6px;
  justify-content: flex-start;
  align-items: stretch;
  flex-wrap: nowrap;
  max-width: none;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: calc(env(safe-area-inset-top, 0px) + 74px) 18px calc(env(safe-area-inset-bottom, 0px) + 28px);
  transition: transform 0.3s ease-out;

  li {
    width: 100%;
  }
`;

const NavList = styled.ul`
  list-style: none;
  display: flex;
  gap: 12px;
  margin: 0;
  padding: 0;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: nowrap;

  li {
    display: flex;
    align-items: center;
  }

  @media (max-width: 768px) {
    ${drawerListStyles}
    transform: translateX(${({ isOpen }) => (isOpen ? "0" : "100%")});
  }
`;

// Rotulo que separa os blocos do menu mobile ("Navegar" / "Mais"). Sem ele a
// lista rolavel vira uma parede de 13 links iguais.
const MobileNavSection = styled.li`
  display: none !important;

  @media (max-width: 768px) {
    display: block !important;
    margin: 14px 0 2px;
    padding: 0 6px;
    color: var(--text-secondary);
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.10em;
    text-transform: uppercase;
    opacity: 0.75;
  }
`;

// Trava o scroll da pagina enquanto o drawer mobile esta aberto - senao o
// conteudo atras rola junto com o menu.
function useBodyScrollLock(locked) {
  useEffect(() => {
    if (!locked) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [locked]);
}

const StyledNavLink = styled(NavLink)`
  color: var(--text-secondary);
  font-weight: 500;
  text-decoration: none;
  font-size: 0.96rem;
  padding: 8px 12px;
  border-radius: 12px;
  transition: background-color 0.3s ease, color 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease;
  position: relative;
  overflow: hidden;
  white-space: nowrap;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, rgba(34, 184, 209, 0.12) 0%, rgba(34, 184, 209, 0.12) 100%);
    border-radius: 12px;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: -1;
  }

  &.active {
    color: #22b8d1;
    font-weight: 700;
    background: linear-gradient(135deg, rgba(34, 184, 209, 0.12) 0%, rgba(34, 184, 209, 0.12) 100%);
    box-shadow: 0 3px 10px rgba(34, 184, 209, 0.14);
    &::before {
        opacity: 1;
    }
  }

  &:hover {
    color: var(--text-primary);
    transform: translateY(-2px);
    &::before {
        opacity: 1;
    }
  }

  @media (max-width: 1180px) {
    font-size: 0.9rem;
    padding: 8px 10px;
  }

  /* No mobile o link era width 80% + text-align center + white-space nowrap
     DENTRO de um overflow hidden - qualquer rotulo mais largo que 80% da tela
     (ex.: "Revisão de Currículo" a 1.2rem num aparelho estreito) era cortado
     no meio, sem reticencias. Agora ocupa a largura toda do drawer, alinha a
     esquerda e pode quebrar linha. */
  @media (max-width: 768px) {
    width: 100%;
    text-align: left;
    padding: 13px 14px;
    font-size: 1.02rem;
    font-weight: 600;
    white-space: normal;
    overflow-wrap: anywhere;
    border-radius: 12px;
  }
`;

const DesktopOnlyNavItem = styled.li`
  @media (max-width: 768px) {
    display: none !important;
  }
`;

const MobileOnlyNavItem = styled.li`
  display: none !important;

  @media (max-width: 768px) {
    display: flex !important;
  }
`;

const MoreMenuWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const MoreMenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 8px 12px;
  border: 1px solid ${({ $open }) => ($open ? "rgba(34, 184, 209, 0.28)" : "var(--border-color)")};
  border-radius: 12px;
  background: ${({ $open }) => ($open ? "var(--brand-soft)" : "var(--surface-primary)")};
  color: var(--text-primary);
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;

  &:hover {
    background: var(--brand-soft);
    border-color: rgba(34, 184, 209, 0.28);
  }

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.2s ease;
    transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
  }
`;

const MoreMenuPanel = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  min-width: 220px;
  padding: 10px;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--surface-primary);
  box-shadow: var(--nav-shadow);
  display: flex;
  flex-direction: column;
  gap: 6px;
  z-index: 1100;
`;

const MoreMenuLink = styled(NavLink)`
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 10px 12px;
  border-radius: 10px;
  text-decoration: none;
  color: var(--text-secondary);
  font-size: 0.95rem;
  font-weight: 600;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover,
  &.active {
    background: var(--brand-soft);
    color: #22b8d1;
  }
`;

const LogoutButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  background: var(--surface-primary);
  border: 1px solid #fecaca;
  color: #991b1b;
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 900;
  font-size: 0.98rem;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease, color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: #f87171;
    background: #fef2f2;
    color: #7f1d1d;
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 13px 15px;
    font-size: 1rem;
    margin-top: 18px;
  }
`;

const DesktopLogoutButton = styled(LogoutButton)`
  @media (max-width: 768px) {
    display: none !important;
  }
`;

const MobileLogoutButton = styled(LogoutButton)`
  display: none;

  @media (max-width: 768px) {
    display: inline-flex;
  }
`;

const PublicNavCta = styled(StyledNavLink)`
  background: #22B8D1;
  color: #ffffff !important;
  font-weight: 800;

  &:hover {
    color: #ffffff !important;
    background: #163589;
  }

  @media (max-width: 768px) {
    text-align: center;
    justify-content: center;
    font-size: 1rem;
    padding: 14px 12px;
  }
`;

const MentorNavLink = styled(StyledNavLink)`
  background: #22b8d1 !important;
  color: #0A1424 !important;
  border: 1px solid rgba(5, 5, 5, 0.08);
  font-weight: 900 !important;
  box-shadow: 0 8px 18px rgba(34, 184, 209, 0.22);

  &:hover {
    color: #0A1424 !important;
    background: #8FE3F2 !important;
  }
`;

const PublicNavLink = styled(StyledNavLink)``;

const PublicMentorNavLink = styled(PublicNavLink)`
  background: #22b8d1 !important;
  color: #0A1424 !important;
  border: 1px solid rgba(5, 5, 5, 0.08);
  font-weight: 900 !important;
  box-shadow: 0 8px 18px rgba(34, 184, 209, 0.22);

  &:hover {
    color: #0A1424 !important;
    background: #8FE3F2 !important;
  }
`;

// O menu publico (deslogado) nao tinha hamburguer nenhum: os 9 itens
// simplesmente quebravam em varias linhas espremidas embaixo da logo no
// celular. Agora ele usa o MESMO drawer do menu logado.
const PublicNavList = styled.ul`
  list-style: none;
  display: flex;
  gap: 14px;
  margin: 0;
  padding: 0;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    ${drawerListStyles}
    transform: translateX(${({ isOpen }) => (isOpen ? "0" : "100%")});
  }
`;

const PublicNavbar = styled(Navbar)``;

const PublicBrandTitle = styled.span`
  color: var(--text-primary);
  font-size: 1.35rem;
  font-weight: 900;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 1.15rem;
  }

  @media (max-width: 380px) {
    display: none;
  }
`;

// Na area logada o mobile ja mostra o nome da secao atual ao lado da logo -
// repetir "QA Iniciante" ali dentro so espremeria os dois textos.
const AuthBrandTitle = styled(PublicBrandTitle)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const LogoNavbar = styled.img`
  height: 60px;
  width: auto;
  margin-right: 0;

  @media (max-width: 768px) {
    height: 40px;
  }
`;

const PublicDropdownWrapper = styled(MoreMenuWrapper)``;

const PublicDropdownButton = styled(MoreMenuButton)``;

const PublicDropdownPanel = styled(MoreMenuPanel)``;

const PublicDropdownLink = styled(MoreMenuLink)``;

const PUBLIC_PRIMARY_LINKS = [
  { to: "/", label: "Home" },
  { to: "/mentoria", label: "Mentoria", highlight: true },
  { to: "/posts", label: "Artigos" },
  { to: "/revisao-curriculo", label: "Revisão de Currículo" },
  { to: "/assinaturas", label: "Planos" },
  { to: "/sobre", label: "Sobre" },
];

const PUBLIC_EXPLORE_LINKS = [
  { to: "/workspace-qa", label: "Central QA" },
  { to: "/mapa-de-carreira-qa", label: "Roadmap QA" },
  { to: "/ebooks", label: "E-books" },
  { to: "/produtos", label: "Ferramentas" },
  { to: "/beneficios", label: "Benefícios" },
];

const PublicExploreMenu = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <PublicDropdownWrapper>
      <PublicDropdownButton
        type="button"
        $open={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        Explorar
        <ChevronDownIcon />
      </PublicDropdownButton>
      {isOpen && (
        <PublicDropdownPanel>
          {PUBLIC_EXPLORE_LINKS.map(({ to, label }) => (
            <PublicDropdownLink key={to} to={to} onClick={() => setIsOpen(false)}>
              {label}
            </PublicDropdownLink>
          ))}
        </PublicDropdownPanel>
      )}
    </PublicDropdownWrapper>
  );
};


const PublicLayout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const closeMenu = () => setIsMenuOpen(false);

  useBodyScrollLock(isMenuOpen);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname, location.hash]);

  return (
    <>
      <PublicNavbar>
        <PublicBrandLink to="/" onClick={closeMenu}>
          <NavHeaderContent>
            <LogoNavbar src={keynsethLogo} alt="Logo QA Iniciante" />
            <PublicBrandTitle>QA Iniciante</PublicBrandTitle>
          </NavHeaderContent>
        </PublicBrandLink>

        <MenuToggle onClick={() => setIsMenuOpen((current) => !current)}>
          {isMenuOpen ? <CloseIcon aria-label="Fechar menu" /> : <MenuIcon aria-label="Abrir menu" />}
        </MenuToggle>

        <PublicNavList isOpen={isMenuOpen}>
          <MobileNavSection>Navegar</MobileNavSection>
          {PUBLIC_PRIMARY_LINKS.map(({ to, label, highlight }) => {
            const NavigationLink = highlight ? PublicMentorNavLink : PublicNavLink;
            return (
              <li key={to}>
                <NavigationLink to={to} onClick={closeMenu}>{label}</NavigationLink>
              </li>
            );
          })}

          <DesktopOnlyNavItem><PublicExploreMenu /></DesktopOnlyNavItem>

          <MobileNavSection>Explorar</MobileNavSection>
          {PUBLIC_EXPLORE_LINKS.map(({ to, label }) => (
            <MobileOnlyNavItem key={`mobile-${to}`}>
              <PublicNavLink to={to} onClick={closeMenu}>{label}</PublicNavLink>
            </MobileOnlyNavItem>
          ))}

          <li><ThemeToggleControl /></li>
          <li><PublicNavCta to="/login" onClick={closeMenu}>Entrar</PublicNavCta></li>
        </PublicNavList>
      </PublicNavbar>
      {children}
    </>
  );
};

const PublicRoutes = ({ onAuthenticated }) => (
  <Suspense fallback={<RouteFallback>Carregando…</RouteFallback>}>
  <Routes>
    <Route path="/" element={<HomePage />} />
    <Route path="/login" element={<AuthPage onAuthenticated={onAuthenticated} />} />
    <Route path="/signup" element={<AuthPage onAuthenticated={onAuthenticated} />} />
    <Route path="/assinaturas" element={<Assinaturas publicMode />} />
    <Route path="/ebooks" element={<EbookStorePage />} />
    <Route path="/beneficios" element={<BenefitsPage />} />
    <Route path="/sobre" element={<AboutPage />} />
    <Route path="/posts" element={<ArticlesPage />} />
    <Route path="/posts/admin" element={<ArticleAdminPage />} />
    <Route path="/posts/:slug" element={<ArticleDetailPage />} />
    <Route path="/artigos" element={<Navigate to="/posts" replace />} />
    <Route path="/artigos/admin" element={<Navigate to="/posts/admin" replace />} />
    <Route path="/artigos/:slug" element={<LegacyArticleRedirect />} />
    <Route path="/biblioteca" element={<Navigate to="/login" replace />} />
    <Route path="/biblioteca/ler/:bookId" element={<Navigate to="/login" replace />} />
    <Route path="/biblioteca/ler/:bookId/:fileIndex" element={<Navigate to="/login" replace />} />
    <Route path="/produtos" element={<Navigate to="/login" replace />} />
    <Route path="/produtos/:productSlug" element={<Navigate to="/login" replace />} />
    <Route path="/mapa-de-carreira-qa" element={<RoadmapPage />} />
    <Route path="/quality-gate" element={<ChecklistPage />} />
    <Route path="/workspace-qa" element={<WorkspaceQA />} />
    <Route path="/template-bug" element={<TemplateBug />} />
    <Route path="/planner-qa" element={<PlannerQA />} />
    <Route path="/mentoria" element={<MentoriaPage />} />
    <Route path="/revisao-curriculo" element={<CurriculoPage />} />
    <Route path="/confirmacao" element={<ConfirmacaoPage />} />
    <Route path="/agendamento" element={<AgendamentoPage />} />
    <Route path="/agendamento-confirmado" element={<AgendamentoConfirmadoPage />} />
    <Route path="/politica-privacidade" element={<PoliticaPrivacidade />} />
    <Route path="/termos-de-uso" element={<TermosDeUso />} />
    <Route path="/contato" element={<Contato />} />
    <Route path="/faq" element={<FAQ />} />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
  </Suspense>
);

const AuthHeaderContainer = styled.div`
  width: 100%;
  margin: 0;
  padding: 30px 30px 24px;
  background:
    radial-gradient(circle at top right, rgba(34, 184, 209, 0.15), transparent 42%),
    linear-gradient(180deg, #ffffff, #f4f7fc);
  border-bottom: 1px solid rgba(231, 237, 247, 0.95);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const AuthHeaderBadge = styled.span`
  color: #0B7F93;
  background: rgba(34, 184, 209, 0.12);
  border: 1px solid rgba(34, 184, 209, 0.24);
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 12px;
`;

const AuthTrustList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 18px;
`;

const AuthTrustItem = styled.span`
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  background: #eff6ff;
  border: 1px solid #dff6fa;
  color: #0B7F93;
  font-size: 0.75rem;
  font-weight: 850;
  display: inline-flex;
  align-items: center;
`;

const AuthFooterContainer = styled.div`
  padding: 0 28px 26px;
  text-align: center;
`;

const AuthFooterText = styled.p`
  margin: 0;
  color: #7482a0;
  font-size: 0.82rem;
  line-height: 1.55;
`;

const AuthFooterLinks = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 10px;
`;

const AuthFooterLink = styled(Link)`
  color: #0B7F93;
  font-size: 0.8rem;
  font-weight: 850;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const AuthInlineButton = styled.button`
  background: transparent;
  border: 0;
  color: #0B7F93;
  cursor: pointer;
  font-size: 0.84rem;
  font-weight: 850;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const PublicBrandLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  min-width: 0;
  flex: 1 1 auto;
`;

const ThemeToggleButton = styled.button`
  min-height: 40px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid var(--border-color);
  background: var(--surface-secondary);
  color: var(--text-primary);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.88rem;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    background: var(--brand-soft);
    border-color: rgba(34, 184, 209, 0.28);
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    min-height: 46px;
    margin-top: 18px;
  }
`;

const ThemeToggleControl = () => {
  const { themeMode, toggleTheme } = useThemeMode();
  const isDark = themeMode === "dark";

  return (
    <ThemeToggleButton type="button" onClick={toggleTheme} aria-label={`Ativar modo ${isDark ? "claro" : "escuro"}`}>
      {isDark ? <SunIcon /> : <MoonIcon />}
      {isDark ? "Modo claro" : "Modo escuro"}
    </ThemeToggleButton>
  );
};

const SignInHeader = () => (
  <AuthHeaderContainer>
    <BackButton />
    <Logo src={keynsethLogo} alt="Logo QA Iniciante" />
    <AuthHeaderBadge>Área do aluno</AuthHeaderBadge>
    <Title>Bem-vindo de volta!</Title>
    <Subtitle>Entre para acessar seus planos, ferramentas, comunidade e materiais.</Subtitle>
    <AuthTrustList aria-label="Benefícios do acesso">
      <AuthTrustItem>Login seguro</AuthTrustItem>
      <AuthTrustItem>Google ou e-mail</AuthTrustItem>
      <AuthTrustItem>Acesso aos materiais</AuthTrustItem>
    </AuthTrustList>
  </AuthHeaderContainer>
);

const SignUpHeader = () => {
  useEffect(() => {
    trackEvent("signup_started");
  }, []);

  return (
    <AuthHeaderContainer>
      <BackButton />
      <Logo src={keynsethLogo} alt="Logo QA Iniciante" />
      <AuthHeaderBadge>Área do aluno</AuthHeaderBadge>
      <Title>Crie sua conta gratuita e evolua em QA</Title>
      <Subtitle>Salve seu progresso e libere as ferramentas com IA do seu plano.</Subtitle>
      <AuthFooterText>
        Depois de preencher, enviaremos um código de confirmação para o seu e-mail. Confira também o spam.
      </AuthFooterText>
      <AuthTrustList aria-label="Benefícios do cadastro">
        <AuthTrustItem>Conta gratuita</AuthTrustItem>
        <AuthTrustItem>Confirmação por e-mail</AuthTrustItem>
        <AuthTrustItem>Área protegida</AuthTrustItem>
      </AuthTrustList>
    </AuthHeaderContainer>
  );
};

const ConfirmHeader = () => (
  <AuthHeaderContainer>
    <BackButton />
    <Logo src={keynsethLogo} alt="Logo QA Iniciante" />
    <AuthHeaderBadge>Confirmação de acesso</AuthHeaderBadge>
    <Title>Verifique seu e-mail</Title>
    <Subtitle>
      Enviamos um código de verificação para a sua caixa de entrada. Por favor, insira-o abaixo
      para continuar.
    </Subtitle>
  </AuthHeaderContainer>
);

const AuthFooter = () => (
  <AuthFooterContainer>
    <AuthFooterText>
      Ao continuar, você acessa a área protegida do QA Iniciante.
    </AuthFooterText>
    <AuthFooterLinks>
      <AuthFooterLink to="/termos-de-uso">Termos de uso</AuthFooterLink>
      <AuthFooterLink to="/politica-privacidade">Política de privacidade</AuthFooterLink>
      <AuthFooterLink to="/contato">Contato</AuthFooterLink>
    </AuthFooterLinks>
  </AuthFooterContainer>
);

const AuthTestimonial = styled.figure`
  margin: 0 0 16px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #eff6ff;
  border: 1px solid #dff6fa;
  text-align: left;
`;

const AuthTestimonialQuote = styled.blockquote`
  margin: 0;
  color: #34425f;
  font-size: 0.86rem;
  line-height: 1.55;
`;

const AuthTestimonialAuthor = styled.figcaption`
  margin-top: 8px;
  color: #0B7F93;
  font-size: 0.78rem;
  font-weight: 800;
`;

const SignUpFooter = () => (
  <>
    <AuthFooterContainer style={{ paddingBottom: 0 }}>
      <AuthTestimonial>
        <AuthTestimonialQuote>
          “Rogério, 1 semana depois que terminamos a mentoria, fui chamado para uma entrevista e hoje fiquei sabendo que fui aprovado. Muito obrigado, a mentoria me ajudou demais.”
        </AuthTestimonialQuote>
        <AuthTestimonialAuthor>Aluno(a) da mentoria</AuthTestimonialAuthor>
      </AuthTestimonial>
    </AuthFooterContainer>
    <AuthFooter />
  </>
);

const SignInFooter = () => {
  const { toForgotPassword } = useAuthenticator();

  return (
    <AuthFooterContainer>
      <AuthInlineButton type="button" onClick={toForgotPassword}>
        Esqueci minha senha
      </AuthInlineButton>
      <AuthFooterLinks>
        <AuthFooterLink to="/termos-de-uso">Termos de uso</AuthFooterLink>
        <AuthFooterLink to="/politica-privacidade">Política de privacidade</AuthFooterLink>
      </AuthFooterLinks>
    </AuthFooterContainer>
  );
};

const BackToSignInFooter = () => {
  const { toSignIn } = useAuthenticator();

  return (
    <AuthFooterContainer>
      <AuthInlineButton type="button" onClick={toSignIn}>
        Voltar para o login
      </AuthInlineButton>
      <AuthFooterLinks>
        <AuthFooterLink to="/contato">Preciso de ajuda</AuthFooterLink>
      </AuthFooterLinks>
    </AuthFooterContainer>
  );
};

const authFormFields = {
  signIn: {
    username: {
      label: "E-mail",
      placeholder: "seuemail@exemplo.com",
      isRequired: true,
    },
    password: {
      label: "Senha",
      placeholder: "Digite sua senha",
      isRequired: true,
    },
  },
  signUp: {
    name: {
      label: "Nome",
      placeholder: "Seu nome completo",
      order: 1,
      isRequired: true,
    },
    email: {
      label: "E-mail",
      placeholder: "seuemail@exemplo.com",
      order: 2,
      isRequired: true,
    },
    phone_number: {
      label: "WhatsApp",
      placeholder: "+55 11 99999-9999",
      order: 3,
    },
    password: {
      label: "Senha",
      placeholder: "Crie uma senha segura",
      order: 4,
      isRequired: true,
    },
    confirm_password: {
      label: "Confirmar senha",
      placeholder: "Repita sua senha",
      order: 5,
      isRequired: true,
    },
  },
  confirmSignUp: {
    confirmation_code: {
      label: "Código de confirmação",
      placeholder: "Digite o código recebido por e-mail",
      isRequired: true,
    },
  },
  resetPassword: {
    username: {
      label: "E-mail",
      placeholder: "seuemail@exemplo.com",
      isRequired: true,
    },
  },
  confirmResetPassword: {
    confirmation_code: {
      label: "Código de confirmação",
      placeholder: "Digite o código recebido por e-mail",
      isRequired: true,
    },
    password: {
      label: "Nova senha",
      placeholder: "Crie uma nova senha",
      isRequired: true,
    },
    confirm_password: {
      label: "Confirmar nova senha",
      placeholder: "Repita a nova senha",
      isRequired: true,
    },
  },
};

const MainContentWithNav = ({ user, onSignOut }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [profileNotice, setProfileNotice] = useState("");
  const [billingSubscription, setBillingSubscription] = useState(null);
  // Enquanto isso fica false, effectiveSubscriptionPlan ainda pode estar no
  // valor de fallback (token do Cognito, que so chega depois de OUTRO fetch
  // assincrono, ou "free" se nada carregou ainda) - nao e o plano real
  // confirmado pelo backend. Paginas que bloqueiam/liberam acesso com base
  // no plano usam essa flag pra evitar mostrar "Free"/bloqueado por um
  // instante pra quem na verdade e assinante, antes do fetch responder.
  const [subscriptionLoaded, setSubscriptionLoaded] = useState(false);
  const [currentUserAttributes, setCurrentUserAttributes] = useState({});
  const location = useLocation();
  const { effectiveSubscriptionPlan, hasPaidPlan, hasPremiumAccess } = useMemo(() => {
    const premiumAccessFlag = String(
      currentUserAttributes['custom:premium_access'] ||
      currentUserAttributes['custom:payment_confirmed'] ||
      currentUserAttributes['custom:community_access'] ||
      currentUserAttributes['custom:subscription_status'] ||
      ''
    ).toLowerCase();
    const localTestPlan = getLocalTestPlan();
    const billingPlan = normalizePlanId(billingSubscription?.plan || "");
    const tokenPlan = normalizePlanId(String(currentUserAttributes['custom:subscription_plan'] || '').toLowerCase());
    const subscriptionPlan = normalizePlanId(localTestPlan || billingPlan || tokenPlan);
    const hasPremiumAccess =
      Boolean(localTestPlan) ||
      billingSubscription?.status === "active" ||
      ['true', '1', 'paid', 'active', 'premium'].includes(premiumAccessFlag);
    const effectivePlan = subscriptionPlan || (hasPremiumAccess ? "starter" : "free");
    return {
      effectiveSubscriptionPlan: effectivePlan,
      hasPaidPlan: isPaidPlanId(effectivePlan),
      hasPremiumAccess,
    };
  }, [currentUserAttributes, billingSubscription]);

  useEffect(() => {
    if (!user?.userId) return;

    fetchUserAttributes()
      .then((attributes) => {
        if (attributes && Object.keys(attributes).length > 0) {
          setCurrentUserAttributes(attributes);
        }
      })
      .catch(() => undefined);
  }, [user?.userId]);

  useEffect(() => {
    if (!user?.userId) {
      setBillingSubscription(null);
      setSubscriptionLoaded(true);
      return;
    }

    setSubscriptionLoaded(false);
    apiRequest("/api/subscriptions/status")
      .then((payload) => setBillingSubscription(payload.data))
      .catch(() => setBillingSubscription(null))
      .finally(() => setSubscriptionLoaded(true));
  }, [user]);

  useBodyScrollLock(isMenuOpen);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    setIsMenuOpen(false);
    setIsMoreMenuOpen(false);
    setProfileNotice("");
  }, [location.pathname, location.hash]);

  const handleUpdateUser = async (updatedFormData) => {
    try {
      const currentUserSession = await getCurrentUser();
      if (!currentUserSession?.userId) {
        setProfileNotice("Você precisa estar logado para atualizar seu perfil.");
        return false;
      }

      const attributesToUpdate = {
        name: `${updatedFormData.firstName || ''} ${updatedFormData.lastName || ''}`.trim(),
        email: updatedFormData.email || '',
        phone_number: updatedFormData.phone || '',
      };

      await updateUserAttributes({ userAttributes: attributesToUpdate });
      const updatedAttrs = await fetchUserAttributes();
      setCurrentUserAttributes(updatedAttrs);
      setProfileNotice("");
      return true;
    } catch (error) {
      setProfileNotice("Não foi possível atualizar seu perfil agora.");
      return false;
    }
  };

  const getSectionName = (pathname) => {
    if (pathname.startsWith("/biblioteca/ler/")) return "Leitor";
    if ((pathname.startsWith("/artigos/") && pathname !== "/artigos/admin") || (pathname.startsWith("/posts/") && pathname !== "/posts/admin")) return "Post";

    switch (pathname) {
      case "/ebooks": return "E-books";
      case "/biblioteca": return "Biblioteca";
      case "/mentoria": return "Mentoria";
      case "/revisao-curriculo": return "Revisão de currículo";
      case "/beneficios": return "Benefícios";
      case "/sobre": return "Sobre";
      case "/artigos": return "Artigos";
      case "/artigos/admin": return "Publicar post";
      case "/posts": return "Artigos";
      case "/posts/admin": return "Publicar post";
      case "/whatsapp": return "Comunidade WhatsApp";
      case "/assinaturas": return "Assinaturas";
      case "/mapa-de-carreira-qa": return "Mapa de Carreira QA";
      case "/quality-gate": return "Checklist de Testes"; // Adicionado
      case "/produtos": return "Ferramentas";
      case "/workspace-qa": return "Central QA";
      case "/template-bug": return "Template de Bug";
      case "/planner-qa": return "Planner QA";
      case "/agendamento": return "Agendamento";
      case "/confirmacao": return "Confirmação";
      case "/agendamento-confirmado": return "Agendamento confirmado";
      case "/profile": return "Meu Perfil";
      case "/politica-privacidade": return "Politica de Privacidade";
      case "/termos-de-uso": return "Termos de Uso";
      case "/contato": return "Contato";
      case "/faq": return "FAQ";
      case "/": return "Início";
      default: return "Página não encontrada";
    }
  };

  const currentSectionName = getSectionName(location.pathname);

  const primaryNavItems = [
    { to: "/", label: "Home" },
    { to: "/mentoria", label: "Mentoria", highlight: true },
    { to: "/posts", label: "Artigos" },
    { to: "/produtos", label: "Ferramentas" },
    { to: "/workspace-qa", label: "Central QA" },
    { to: "/assinaturas", label: "Planos" },
    ...(hasPremiumAccess ? [{ to: "/whatsapp", label: "Comunidade" }] : []),
  ];

  const secondaryNavItems = [
    { to: "/mapa-de-carreira-qa", label: "Roadmap QA" },
    { to: "/revisao-curriculo", label: "Revisão de Currículo" },
    { to: "/beneficios", label: "Benefícios" },
    ...(hasPaidPlan
      ? [{ to: "/biblioteca", label: "Biblioteca" }]
      : [{ to: "/ebooks", label: "E-books" }]),
    { to: "/sobre", label: "Sobre" },
    { to: "/profile", label: "Minha conta" },
  ];

  return (
    <>
      <Navbar>
        {/* Logo e nome do site nao eram clicaveis na area logada (so o menu
            publico tinha o link) - nao existia jeito de voltar pra home
            tocando na marca, que e o gesto que todo mundo tenta primeiro. */}
        <PublicBrandLink to="/" onClick={() => setIsMenuOpen(false)}>
          <NavHeaderContent>
            <LogoNavbar src={keynsethLogo} alt="Logo QA Iniciante" />
            <AuthBrandTitle>QA Iniciante</AuthBrandTitle>
            <CurrentPageTitle>{currentSectionName}</CurrentPageTitle>
          </NavHeaderContent>
        </PublicBrandLink>

        {user && (
          <>
            <MenuToggle onClick={toggleMenu}>
              {isMenuOpen ? (
                <CloseIcon aria-label="Fechar menu" />
              ) : (
                <MenuIcon aria-label="Abrir menu" />
              )}
            </MenuToggle>

            <NavList isOpen={isMenuOpen}>
              {primaryNavItems.map(({ to, label, highlight }) => {
                const NavigationLink = highlight ? MentorNavLink : StyledNavLink;

                return (
                  <DesktopOnlyNavItem key={to}>
                    <NavigationLink to={to}>{label}</NavigationLink>
                  </DesktopOnlyNavItem>
                );
              })}

              <DesktopOnlyNavItem>
                <MoreMenuWrapper>
                  <MoreMenuButton
                    type="button"
                    $open={isMoreMenuOpen}
                    onClick={() => setIsMoreMenuOpen((current) => !current)}
                  >
                    Mais
                    <ChevronDownIcon />
                  </MoreMenuButton>
                  {isMoreMenuOpen && (
                    <MoreMenuPanel>
                      {secondaryNavItems.map(({ to, label }) => (
                        <MoreMenuLink key={to} to={to} onClick={() => setIsMoreMenuOpen(false)}>
                          {label}
                        </MoreMenuLink>
                      ))}
                    </MoreMenuPanel>
                  )}
                </MoreMenuWrapper>
              </DesktopOnlyNavItem>

              <DesktopOnlyNavItem>
                <ThemeToggleControl />
              </DesktopOnlyNavItem>

              <DesktopOnlyNavItem>
                <DesktopLogoutButton onClick={onSignOut}>
                  <LogOutIcon size={16} />
                  Sair
                </DesktopLogoutButton>
              </DesktopOnlyNavItem>

              <MobileNavSection>Navegar</MobileNavSection>
              {primaryNavItems.map(({ to, label, highlight }) => {
                const NavigationLink = highlight ? MentorNavLink : StyledNavLink;

                return (
                  <MobileOnlyNavItem key={`mobile-${to}`}>
                    <NavigationLink to={to} onClick={() => setIsMenuOpen(false)}>
                      {label}
                    </NavigationLink>
                  </MobileOnlyNavItem>
                );
              })}

              <MobileNavSection>Mais</MobileNavSection>
              {secondaryNavItems.map(({ to, label }) => (
                <MobileOnlyNavItem key={`mobile-${to}`}>
                  <StyledNavLink to={to} onClick={() => setIsMenuOpen(false)}>
                    {label}
                  </StyledNavLink>
                </MobileOnlyNavItem>
              ))}

              <MobileOnlyNavItem>
                <ThemeToggleControl />
              </MobileOnlyNavItem>

              <MobileOnlyNavItem>
                <MobileLogoutButton onClick={onSignOut}>
                  <LogOutIcon size={16} />
                  Sair
                </MobileLogoutButton>
              </MobileOnlyNavItem>
            </NavList>
          </>
        )}
      </Navbar>

      {profileNotice ? (
        <div style={{ maxWidth: 1180, margin: "16px auto 0", padding: "0 20px" }}>
          <InlineAlert tone="warning" title="Atualizacao indisponivel">
            {profileNotice}
          </InlineAlert>
        </div>
      ) : null}

      <Suspense fallback={<RouteFallback>Carregando…</RouteFallback>}>
      <Routes>
        <Route path="/" element={<HomePage authenticated />} />
        <Route path="/posts" element={<ArticlesPage />} />
        <Route path="/posts/admin" element={<ArticleAdminPage />} />
        <Route path="/posts/:slug" element={<ArticleDetailPage />} />
        <Route path="/artigos" element={<Navigate to="/posts" replace />} />
        <Route path="/artigos/admin" element={<Navigate to="/posts/admin" replace />} />
        <Route path="/artigos/:slug" element={<LegacyArticleRedirect />} />
        <Route path="/mapa-de-carreira-qa" element={<RoadmapPage />} />
        <Route path="/quality-gate" element={<ChecklistPage />} />
        <Route path="/workspace-qa" element={<WorkspaceQA />} />
        <Route path="/template-bug" element={<TemplateBug />} />
        <Route path="/planner-qa" element={<PlannerQA />} />
        <Route path="/mentoria" element={<MentoriaPage />} />
        <Route path="/revisao-curriculo" element={<CurriculoPage />} />
        <Route path="/beneficios" element={<BenefitsPage />} />
        <Route path="/sobre" element={<AboutPage />} />
        <Route path="/confirmacao" element={<ConfirmacaoPage />} />
        <Route path="/agendamento-confirmado" element={<AgendamentoConfirmadoPage />} />
        <Route path="/agendamento" element={<AgendamentoPage />} />
        <Route
          path="/assinaturas"
          element={
            <Assinaturas
              subscriptionPlan={effectiveSubscriptionPlan}
              subscriptionStatus={billingSubscription?.status || (hasPaidPlan ? "active" : "free")}
              subscriptionLoaded={subscriptionLoaded}
            />
          }
        />
        <Route path="/ebooks" element={<EbookStorePage />} />
        <Route path="/biblioteca" element={<Ebooks subscriptionPlan={effectiveSubscriptionPlan} />} />
        <Route path="/biblioteca/ler/:bookId" element={<EbookReaderPage subscriptionPlan={effectiveSubscriptionPlan} />} />
        <Route path="/biblioteca/ler/:bookId/:fileIndex" element={<EbookReaderPage subscriptionPlan={effectiveSubscriptionPlan} />} />
        <Route path="/produtos" element={<ProductsHub subscriptionPlan={effectiveSubscriptionPlan} subscriptionLoaded={subscriptionLoaded} />} />
        <Route path="/produtos/:productSlug" element={<PaidProductPage subscriptionPlan={effectiveSubscriptionPlan} subscriptionLoaded={subscriptionLoaded} />} />
        <Route path="/profile" element={
              <Profile
                userData={{
                  firstName: currentUserAttributes.name?.split(" ")[0] || "",
                  lastName: currentUserAttributes.name?.split(" ").slice(1).join(" ") || "",
                  email: currentUserAttributes.email || "",
                  phone: currentUserAttributes.phone_number || "",
                }}
                subscriptionPlan={effectiveSubscriptionPlan}
                subscriptionStatus={billingSubscription?.status || (hasPaidPlan ? "active" : "free")}
                subscription={billingSubscription}
                subscriptionLoaded={subscriptionLoaded}
                onUpdate={handleUpdateUser}
                onLogout={onSignOut}
              />
        } />

        {hasPremiumAccess && (
          <Route path="/whatsapp" element={<WhatsAppGroup />} />
        )}

        {/*<Route path="*" element={<Navigate to="/ebooks" replace />} />*/}

        <Route path="/politica-privacidade" element={<PoliticaPrivacidade />} />
        <Route path="/termos-de-uso" element={<TermosDeUso />} />
        <Route path="/contato" element={<Contato />} />
        <Route path="/faq" element={<FAQ />} />
        
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </>
  );
};

// /artigos/:slug (URL antiga) -> /posts/:slug
const LegacyArticleRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/posts/${slug}`} replace />;
};

// Meta tags por rota (title/description/canonical/og) sem editar cada página.
// Rotas não mapeadas (ex.: /posts/:slug) definem o próprio SEO na página.
const RouteSeo = () => {
  const { pathname } = useLocation();
  const path = normalizePathname(pathname);

  if (isNoindexPath(path)) {
    return <Seo path={path} noindex />;
  }

  const meta = SEO_META[path];
  if (!meta) return null;

  return <Seo title={meta.title} description={meta.description} path={path} />;
};

// Meta Pixel: o snippet em public/index.html já dispara o PageView da carga
// inicial; aqui cobrimos as navegações seguintes dentro da SPA.
const MetaPixelPageView = () => {
  const { pathname } = useLocation();
  const isFirstRender = React.useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (typeof window.fbq === "function") window.fbq("track", "PageView");
  }, [pathname]);

  return null;
};

const AuthenticatedRedirect = ({ user, onAuthenticated }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;

    onAuthenticated(user);

    // Lê e consome a flag já aqui, para não vazar para um login futuro
    // quando o fluxo sair por plan/next.
    let isNewSignup = false;
    try {
      isNewSignup = window.localStorage.getItem("qa_new_signup") === "1";
      window.localStorage.removeItem("qa_new_signup");
    } catch {
      // ignore
    }

    const params = new URLSearchParams(location.search);
    const requestedPlan = params.get("plan");
    const pendingPlan = normalizePlanId(requestedPlan);

    if (isPaidPlanId(pendingPlan)) {
      window.localStorage.removeItem("qa_pending_subscription_plan");
      navigate(`/assinaturas?plan=${encodeURIComponent(pendingPlan)}&checkout=1`, { replace: true });
      return;
    }

    window.localStorage.removeItem("qa_pending_subscription_plan");
    window.localStorage.removeItem("qa_pending_subscription_reference");
    window.localStorage.removeItem("qa_pending_subscription_checkout_url");

    // "next" leva de volta pra página que pediu login (ex: artigo onde a
    // pessoa tentou curtir/comentar). Só aceita caminho interno começando
    // com uma única "/" pra não virar open redirect.
    const nextPath = params.get("next");
    if (nextPath && nextPath.startsWith("/") && !nextPath.startsWith("//") && !nextPath.startsWith("/\\")) {
      navigate(nextPath, { replace: true });
      return;
    }

    // Recém-cadastrado (flag gravada ao confirmar o código) começa pelas
    // ferramentas; quem só fez login continua indo para /produtos.
    navigate(isNewSignup ? "/workspace-qa" : "/produtos", { replace: true });
  }, [location.search, navigate, onAuthenticated, user]);

  return null;
};

// Confirmação do código de e-mail = conta criada de fato. Marca o usuário como
// recém-cadastrado (usado no redirect pós-login) e dispara signup_completed.
const authServices = {
  async handleConfirmSignUp(input) {
    const result = await confirmSignUp(input);
    try {
      window.localStorage.setItem("qa_new_signup", "1");
    } catch {
      // ignore
    }
    trackEvent("signup_completed");
    return result;
  },
};

const AuthPage = ({ onAuthenticated }) => {
  const initialAuthState = window.location.pathname === "/signup" ? "signUp" : "signIn";

  return (
    <ThemeProvider theme={customTheme}>
      <Authenticator
        initialState={initialAuthState}
        socialProviders={authSocialProviders}
        signUpAttributes={["email", "name", "phone_number"]}
        formFields={authFormFields}
        services={authServices}
        components={{
          SignIn: { Header: SignInHeader, Footer: SignInFooter },
          SignUp: { Header: SignUpHeader, Footer: SignUpFooter },
          ConfirmSignUp: { Header: ConfirmHeader, Footer: AuthFooter },
          ResetPassword: { Header: SignInHeader, Footer: BackToSignInFooter },
          ConfirmResetPassword: { Header: ConfirmHeader, Footer: BackToSignInFooter },
        }}
      >
        {({ user }) => {
          if (user) {
            return <AuthenticatedRedirect user={user} onAuthenticated={onAuthenticated} />;
          }
          return null;
        }}
      </Authenticator>
    </ThemeProvider>
  );
};

const AppRoutes = ({ user, onSignOut, onAuthenticated }) => {
  if (user) {
    return <MainContentWithNav user={user} onSignOut={onSignOut} />;
  }

  return (
    <PublicLayout>
      <PublicRoutes onAuthenticated={onAuthenticated} />
    </PublicLayout>
  );
};

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [themeMode, setThemeMode] = useState(() => {
    if (typeof window === "undefined") return "dark";

    const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (storedTheme === "light" || storedTheme === "dark") {
      return storedTheme;
    }

    return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  });

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      setUser(null);
      // .replace() é melhor para logout, pois remove a página do histórico do navegador.
      window.location.replace("/");
    } catch (error) {
      window.location.replace("/");
    }
  }, []);

  const toggleTheme = () => {
    setThemeMode((currentTheme) => (currentTheme === "dark" ? "light" : "dark"));
  };

  useEffect(() => {
    const checkUser = async () => {
      try {
        const currentUser = await getCurrentUser();
        setUser(currentUser);
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkUser();
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(THEME_STORAGE_KEY, themeMode);
    }
  }, [themeMode]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontSize: '1.5rem',
        color: '#22B8D1'
      }}>
        Carregando...
      </div>
    );
  }

  return (
    <ThemeModeContext.Provider value={{ themeMode, toggleTheme }}>
      <GlobalStyle themeMode={themeMode} />
      <ToastProvider>
        <ConfirmProvider>
          <Router>
            <ScrollToTop />
            <RouteSeo />
            <MetaPixelPageView />
            <AppRoutes user={user} onSignOut={handleSignOut} onAuthenticated={setUser} />
          </Router>
        </ConfirmProvider>
      </ToastProvider>
    </ThemeModeContext.Provider>
  );
}
